import { Inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizeService } from './authorize.service';
import { Store } from '@ngrx/store';
import { AppState } from '../states/app.state';
import { toastrEvents } from 'src/app/extensions/services/api/events';
import { isNotEmpty, uriNormalize } from 'src/app/extensions/utilities';
import { SignalrMessageModel } from '../models/SignalrMessageModel';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { ApplicationAPIService } from 'src/app/extensions/services/api/application-api.service';
import { NotificationAPIService } from 'src/app/extensions/services/api/notification-api.service';
import { ENV_CONFIG } from '../injection.token';
import { AddMessage } from '../actions/message.actions';
import {
  DEFAULT_SIGNALR_AUTORETRY_DELAY,
  DOWNLOAD_FILE_EVENT_TYPES,
} from 'src/app/extensions/constants/commonConstants';
import { FileStoreAPIService } from 'src/app/extensions/services/api/file-store-api.service';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private message$ = new Subject<SignalrMessageModel>();
  private connection: signalR.HubConnection;

  constructor(
    @Inject(ENV_CONFIG) private _envConfig: env.EnvData,
    private _authorizeService: AuthorizeService,
    private _store: Store<AppState>,
    private _notificationService: NotificationService,
    private _translate: TranslateService,
    private _globalService: GlobalService,
    private _applicationAPIService: ApplicationAPIService,
    private _notificationAPIService: NotificationAPIService,
    private _fileStoreAPIService: FileStoreAPIService
  ) {}

  private buildConnection() {
    let applicationId = localStorage.getItem('appId') as string;

    if (applicationId == null || applicationId == undefined) {
      this._applicationAPIService.GetAppId(this._envConfig.APP_CODE).subscribe(applicationId => {
        localStorage.setItem('appId', applicationId);
        return this.buildConnection();
      });
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${uriNormalize(this._envConfig.PORTAL_SIGNALR_API_URL)}/hubs/events?appId=${applicationId}`, {
        accessTokenFactory: () => {
          return this._authorizeService.accessToken;
        },
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds() {
          return DEFAULT_SIGNALR_AUTORETRY_DELAY;
        },
      })
      .build();
    this.connect();
  }

  private connect() {
    this.connection.start().catch(error => console.log(`SIGNAL-R: Could not establish connection. Error: ${error}`));
    this.connection.on('SendServerEvent', message => {
      this.processMessageFromServer(message);
    });
  }

  private processMessageFromServer(message) {
    if (message) {
      console.log(`SIGNAL-R: Received message of type ${message.eventType}`);
      this.message$.next(message);
      var dataMessage = JSON.parse(JSON.stringify(message));
      if (dataMessage.eventType.includes(toastrEvents.WebNotified)) {
        if (isNotEmpty(dataMessage.eventBody.externalId)) {
          this._globalService.obsNewNotification.next(true);
          let numberOfNotifications = this._globalService.numberOfNotifications;
          numberOfNotifications++;
          this._globalService.numberOfNotifications = numberOfNotifications;
          dataMessage.eventBody.notificationBody = this._translate.instant(
            'notification.body.' + dataMessage.eventBody.notificationBody
          );
          let details = JSON.parse(dataMessage.eventBody.notificationDetails);

          if (details != null) {
            Object.entries(details).forEach(detail => {
              dataMessage.eventBody.notificationBody = dataMessage.eventBody.notificationBody.replace(
                `{{${detail[0]}}}`,
                `<b>${detail[1]}</b>`
              );
            });
            if (details.hasOwnProperty('request_link')) {
              dataMessage.eventBody.link = `${this._envConfig.REDIRECT_URI}${details['request_link']}`;
            }
          }
          this._notificationService.showInfo(
            this._translate.instant(dataMessage.eventBody.notificationBody),
            this._translate.instant('notification.body.title.' + dataMessage.eventBody.notificationTitle),
            dataMessage.eventBody.link,
            true,
            dataMessage.eventBody.activityId,
            this._notificationAPIService
          );
        } else {
          this._notificationService.showInfo(
            this._translate.instant('notification.body.' + dataMessage.eventBody.notificationBody),
            this._translate.instant('notification.body.title.' + dataMessage.eventBody.notificationTitle),
            dataMessage.eventBody.link
          );
        }
      } else if (
        DOWNLOAD_FILE_EVENT_TYPES.some(downloadFileEventType => dataMessage.eventType.includes(downloadFileEventType))
      ) {
        this._fileStoreAPIService.getFile(dataMessage.eventBody.fileName).subscribe(result => {
          const downloadLink = document.createElement('a');
          const fileExtension = dataMessage.eventBody.fileName.split('.').pop();
          const documentRef = `data:application/${fileExtension};base64,${result}`;
          downloadLink.href = documentRef;
          downloadLink.download = dataMessage.eventBody.originalName;
          downloadLink.click();
        });
      }
    }
  }

  public enableSignalRSubscription() {
    this.buildConnection();

    this.message$.subscribe(
      (message: SignalrMessageModel) => {
        this._store.dispatch(AddMessage({ payload: message }));
      },
      error => {
        this._notificationService.showError(error, this._translate.instant('signalR.notifyInterceptionFail'));
      }
    );
  }

  public disconnect() {
    this.connection.stop();
  }
}
